import React, { useState } from "react";
import { signIn, confirmSignIn, resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { Eye, EyeOff } from "lucide-react";

const COMMITTEE = "Sri Siddhi Vinayaka Utsava Samithi";

export default function Login({ onLogin }) {
  const [step, setStep] = useState("login"); // login | newPassword | forgotRequest | forgotConfirm
  const [form, setForm] = useState({ email: "", password: "", newPassword: "", code: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const result = await signIn({ username: form.email, password: form.password });
      if (result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        setStep("newPassword");
      } else if (result.isSignedIn) {
        onLogin();
      }
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    if (form.newPassword.length < 8) { setError("Password must be at least 8 characters"); return; }
    setError(""); setLoading(true);
    try {
      const result = await confirmSignIn({ challengeResponse: form.newPassword });
      if (result.isSignedIn) onLogin();
    } catch (err) {
      setError(err.message || "Failed to set password");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await resetPassword({ username: form.email });
      setInfo(`OTP sent to ${form.email}`);
      setStep("forgotConfirm");
    } catch (err) {
      setError(err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotConfirm = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await confirmResetPassword({ username: form.email, confirmationCode: form.code, newPassword: form.newPassword });
      setInfo("Password reset! Please log in.");
      setStep("login");
    } catch (err) {
      setError(err.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 flex flex-col items-center justify-center p-4">
      {/* Logo */}
      <div className="text-center mb-8">
        {/* <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-3 shadow-lg shadow-orange-200">
          🕉️
        </div> */}
        <div className="flex justify-center mb-2">
          <img src="/logo.png" alt="Logo" className="h-16 w-16" />
        </div>
        <h1 className="text-2xl font-black text-gray-900">Ganesh Event Manager</h1>
        <p className="text-sm text-gray-500 mt-1">{COMMITTEE}</p>
      </div>

      {/* Card */}
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 border border-orange-100">
        {/* Step: Login */}
        {step === "login" && (
          <>
            <h2 className="text-base font-bold text-gray-800 mb-5">Sign in to your account</h2>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Email</label>
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                  placeholder="admin@ssvus.org" required autoFocus
                  className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Password</label>
                <div className="relative">
                  <input type={showPw ? "text" : "password"} value={form.password} onChange={(e) => set("password", e.target.value)}
                    placeholder="••••••••" required
                    className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              {error && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
              <button type="submit" disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3 rounded-xl transition-colors shadow-lg shadow-orange-200 flex items-center justify-center gap-2">
                {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : null}
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>
            <button onClick={() => { setStep("forgotRequest"); setError(""); }}
              className="w-full text-xs text-orange-500 hover:text-orange-600 font-semibold mt-4 text-center">
              Forgot password?
            </button>
          </>
        )}

        {/* Step: Set new password (first login) */}
        {step === "newPassword" && (
          <>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-5 text-xs text-orange-700">
              Your account requires a new password to continue.
            </div>
            <form onSubmit={handleNewPassword} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">New Password</label>
                <div className="relative">
                  <input type={showPw ? "text" : "password"} value={form.newPassword} onChange={(e) => set("newPassword", e.target.value)}
                    placeholder="Min 8 chars, upper + number" required
                    className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              {error && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
              <button type="submit" disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3 rounded-xl transition-colors">
                {loading ? "Setting password..." : "Set Password & Continue"}
              </button>
            </form>
          </>
        )}

        {/* Step: Forgot — request OTP */}
        {step === "forgotRequest" && (
          <>
            <h2 className="text-base font-bold text-gray-800 mb-5">Reset your password</h2>
            <form onSubmit={handleForgotRequest} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Email</label>
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                  placeholder="your@email.com" required autoFocus
                  className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
              {error && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
              <button type="submit" disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3 rounded-xl transition-colors">
                {loading ? "Sending OTP..." : "Send OTP"}
              </button>
            </form>
            <button onClick={() => { setStep("login"); setError(""); }}
              className="w-full text-xs text-gray-500 hover:text-gray-700 font-semibold mt-4 text-center">
              ← Back to login
            </button>
          </>
        )}

        {/* Step: Forgot — confirm OTP + new password */}
        {step === "forgotConfirm" && (
          <>
            <h2 className="text-base font-bold text-gray-800 mb-1">Enter OTP</h2>
            {info && <p className="text-xs text-green-600 bg-green-50 px-3 py-2 rounded-lg mb-4">{info}</p>}
            <form onSubmit={handleForgotConfirm} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">OTP (from email)</label>
                <input type="text" value={form.code} onChange={(e) => set("code", e.target.value)}
                  placeholder="6-digit code" required maxLength={6}
                  className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 tracking-widest text-center" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">New Password</label>
                <div className="relative">
                  <input type={showPw ? "text" : "password"} value={form.newPassword} onChange={(e) => set("newPassword", e.target.value)}
                    placeholder="Min 8 chars" required
                    className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              {error && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
              <button type="submit" disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3 rounded-xl transition-colors">
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          </>
        )}
      </div>

      <p className="text-xs text-gray-400 mt-6">Ganesh Chaturthi 2026 • utsav.btrix.in</p>
    </div>
  );
}
